/**
 * Message notifications on this computer (TRUS-668).
 *
 * New messages reach the app as `communication` rows in /api/notifications
 * (services/message_delivery.py writes one per recipient, link
 * `/workspace-messaging?channel=<id>`). NotificationBell polls the summary and
 * raises a native notification for the newest unread one: the Web
 * Notifications API in a browser, @tauri-apps/plugin-notification in the
 * desktop app. This module holds the rules around that:
 *
 *  - On by default: `desktop_notifications_enabled` missing means on; only an
 *    explicit `false` turns it off.
 *  - Not for the conversation the user is reading right now in a focused
 *    window (MessagingWorkspace reports which one is on screen).
 *  - A click opens that conversation in-app. The desktop app's notification
 *    plugin cannot deliver clicks, so (as call notifications do) the next
 *    activation of the window shortly after the notification stands for the
 *    click: clicking the notification activates the app.
 */
/** How long after a desktop notification an app activation still means "open it". */
export const MESSAGE_ACTIVATION_WINDOW_MS = 60_000;

export const isTauriApp = (): boolean => typeof window !== 'undefined' && '__TAURI__' in window;

/** Another tab / app is in front, or the window is hidden / minimized. */
export function isWindowInBackground(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.hidden) return true;
  try {
    return typeof document.hasFocus === 'function' && !document.hasFocus();
  } catch {
    return false;
  }
}

/** Message (and other) desktop notifications are on unless the user switched them off. */
export function messageNotificationsEnabled(prefs: unknown): boolean {
  if (!prefs || typeof prefs !== 'object') return true;
  return (prefs as { desktop_notifications_enabled?: unknown }).desktop_notifications_enabled !== false;
}

// --------------------------------------------------------------------------- //
// the conversation on screen in this window
// --------------------------------------------------------------------------- //
let activeChannelId: number | null = null;

/** MessagingWorkspace: this conversation is on screen (null when none). */
export function setActiveConversation(channelId: number | null): void {
  activeChannelId = channelId && channelId > 0 ? channelId : null;
}

export function getActiveConversation(): number | null {
  return activeChannelId;
}

/** The channel a notification link points at, or null. */
export function channelIdFromLink(link: string | null | undefined): number | null {
  if (!link) return null;
  try {
    const u = new URL(link, 'https://weavein.invalid');
    const raw = u.searchParams.get('channel');
    const legacy = /^\/?messaging\/(\d+)\/?$/.exec(u.pathname);
    const id = Number(raw ?? (legacy ? legacy[1] : NaN));
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** False when the user is already reading this conversation in a focused, visible window. */
export function shouldNotifyForLink(link: string | null | undefined): boolean {
  const channelId = channelIdFromLink(link);
  if (channelId === null || channelId !== activeChannelId) return true;
  return isWindowInBackground();
}

// --------------------------------------------------------------------------- //
// desktop app: the next activation stands for the click
// --------------------------------------------------------------------------- //
let disarmPending: (() => void) | null = null;

/**
 * Desktop app only: run `onActivate` (open the conversation) when the window is
 * next activated within MESSAGE_ACTIVATION_WINDOW_MS, and ask for attention
 * (one dock bounce / taskbar flash). A newer notification replaces the target.
 */
export function armDesktopNotificationClick(onActivate: () => void): void {
  if (!isTauriApp() || typeof window === 'undefined') return;
  disarmPending?.();
  const onFocus = () => {
    disarm();
    void focusMainWindow();
    onActivate();
  };
  const timer = setTimeout(() => disarm(), MESSAGE_ACTIVATION_WINDOW_MS);
  const disarm = () => {
    window.removeEventListener('focus', onFocus);
    clearTimeout(timer);
    if (disarmPending === disarm) disarmPending = null;
  };
  window.addEventListener('focus', onFocus);
  disarmPending = disarm;
  void import('@tauri-apps/api/window')
    .then(({ getCurrentWindow, UserAttentionType }) =>
      getCurrentWindow().requestUserAttention(UserAttentionType.Informational),
    )
    .catch(() => {});
}

async function focusMainWindow(): Promise<void> {
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window');
    const w = getCurrentWindow();
    await w.show().catch(() => {});
    await w.unminimize().catch(() => {});
    await w.setFocus().catch(() => {});
  } catch {
    /* not in the desktop app */
  }
}

/** Tests only. */
export function __resetDesktopMessageNotifications(): void {
  disarmPending?.();
  disarmPending = null;
  activeChannelId = null;
}
